import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { Loader2, Trash2 } from "lucide-react";

import {
  createGrantNote,
  deleteGrantNote,
  type GrantNotable,
  type GrantNote,
  listGrantNotes,
} from "@/api/grant_notes";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

type GrantNotesThreadProps = {
  municipalityId: number;
  opportunityId: number;
  notable: GrantNotable;
  currentUserId: number | null;
  className?: string;
};

function notableKey(notable: GrantNotable): string {
  switch (notable.type) {
    case "milestone":
      return `milestone-${notable.milestoneId}`;
    case "task":
      return `task-${notable.taskId}`;
    default:
      return "opportunity";
  }
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return formatDistanceToNow(date, { addSuffix: true });
}

export function GrantNotesThread({
  municipalityId,
  opportunityId,
  notable,
  currentUserId,
  className,
}: GrantNotesThreadProps) {
  const queryClient = useQueryClient();
  const notesQueryKey = ["grant_notes", municipalityId, opportunityId, notableKey(notable)] as const;
  const [draft, setDraft] = useState("");

  const { data: notes = [], isLoading } = useQuery({
    queryKey: notesQueryKey,
    queryFn: () => listGrantNotes(municipalityId, opportunityId, notable),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: notesQueryKey });

  const createMutation = useMutation({
    mutationFn: (body: string) => createGrantNote(municipalityId, opportunityId, notable, body),
    onSuccess: () => {
      setDraft("");
      refresh();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (noteId: number) => deleteGrantNote(municipalityId, opportunityId, notable, noteId),
    onSuccess: refresh,
  });

  const submit = () => {
    const body = draft.trim();
    if (body) createMutation.mutate(body);
  };

  return (
    <div className={cn("space-y-3", className)}>
      {isLoading ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          Loading notes…
        </p>
      ) : notes.length === 0 ? (
        <p className="text-xs text-muted-foreground">No notes yet.</p>
      ) : (
        <ul className="space-y-2">
          {notes.map((note: GrantNote) => (
            <li key={note.id} className="rounded-md border bg-muted/30 p-3 text-sm">
              <div className="mb-1 flex items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {note.author_name ?? "Removed user"}
                  </span>
                  <span>{formatWhen(note.created_at)}</span>
                </div>
                {note.author_id === currentUserId ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    aria-label={`Delete note by ${note.author_name ?? "you"}`}
                    disabled={deleteMutation.isPending}
                    onClick={() => deleteMutation.mutate(note.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
              </div>
              <p className="whitespace-pre-wrap break-words">{note.body}</p>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-2">
        <Textarea
          value={draft}
          aria-label="Add a note"
          placeholder="Add a note for your team…"
          rows={2}
          className="text-sm"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              submit();
            }
          }}
        />
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            onClick={submit}
            disabled={!draft.trim() || createMutation.isPending}
          >
            Add note
          </Button>
        </div>
      </div>
    </div>
  );
}
